import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { createUserStorage } from './userStorage'

const FEATURES_STORE_KEY = 'features-store'

interface Feature {
  description: string
  value: boolean
}
interface FeaturesStoreState {
  guidelinesFeature: Feature
  guidelineEditorFeature: Feature
  importFeature: Feature
  scoreSettingsFeature: Feature
  analyticsDashboardFeature: Feature
  bulkCancelWorkItemsFeature: Feature
  bulkReopenWorkItemsFeature: Feature
  spanAnnotationFeature: Feature
}

const INITIAL_FEATURES_STORE: FeaturesStoreState = {
  guidelinesFeature: {
    description: 'Enable guidelines feature (space level)',
    value: false,
  },
  guidelineEditorFeature: {
    description: 'Enable WYSIWYG editor for guidelines (PDF viewer is default)',
    value: false,
  },
  importFeature: {
    description: 'Enable import feature (space level)',
    value: false,
  },
  scoreSettingsFeature: {
    description: 'Enable score settings feature (project level)',
    value: false,
  },
  analyticsDashboardFeature: {
    description:
      'Enable analytics dashboards - Operations & Quality Metrics (project level)',
    value: false,
  },
  bulkCancelWorkItemsFeature: {
    description: 'Enable bulk cancel for work items (project level)',
    value: false,
  },
  bulkReopenWorkItemsFeature: {
    description: 'Enable bulk reopen for work items (project level)',
    value: false,
  },
  spanAnnotationFeature: {
    description:
      'Enable span annotations in Taxonomy, Form Builder and Import CSV flow',
    value: false,
  },
}

export const useFeaturesStore = create<FeaturesStoreState>()(
  persist(() => INITIAL_FEATURES_STORE, {
    name: FEATURES_STORE_KEY,
    storage: createUserStorage(),
  }),
)

export const toggleFeature = (featureName: keyof FeaturesStoreState) => {
  useFeaturesStore.setState((state) => ({
    [featureName]: {
      ...state[featureName],
      value: !state[featureName].value,
    },
  }))
}

// Selectors for use outside of React components
export const getIsGuidelinesEnabled = () =>
  useFeaturesStore.getState().guidelinesFeature.value

export const getIsGuidelineEditorEnabled = () =>
  useFeaturesStore.getState().guidelineEditorFeature.value

export const getIsImportEnabled = () =>
  useFeaturesStore.getState().importFeature.value

export const getIsScoreSettingsEnabled = () =>
  useFeaturesStore.getState().scoreSettingsFeature.value

export const getIsAnalyticsDashboardEnabled = () =>
  useFeaturesStore.getState().analyticsDashboardFeature.value

// Selectors for use inside React components (subscribe to store updates)
export const useIsGuidelineEditorEnabled = () =>
  useFeaturesStore((state) => state.guidelineEditorFeature.value)

export const useIsAnalyticsDashboardEnabled = () =>
  useFeaturesStore((state) => state.analyticsDashboardFeature.value)

export const useIsBulkCancelWorkItemsEnabled = () =>
  useFeaturesStore((state) => state.bulkCancelWorkItemsFeature.value)

export const useIsBulkReopenWorkItemsEnabled = () =>
  useFeaturesStore((state) => state.bulkReopenWorkItemsFeature.value)

export const useIsSpanAnnotationEnabled = () =>
  useFeaturesStore((state) => state.spanAnnotationFeature.value)

export const rehydrateFeaturesStore = () => useFeaturesStore.persist.rehydrate()

export const resetFeaturesStore = () =>
  useFeaturesStore.setState(INITIAL_FEATURES_STORE, true)
